import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Signs the user out after 15 minutes without activity.
 *
 * After 14 idle minutes a warning counts down the last minute and lets the
 * user stay signed in. At 15 minutes the stored token is cleared and the app
 * is covered by a "session expired" screen; if nobody clicks "Sign in again"
 * within AUTO_SIGN_OUT_SECONDS, the user is signed out anyway. Signing out
 * inside the portal sends it LOGOUT (the message the staff portal acts on:
 * it clears its login and returns to its start page); opened on its own,
 * the app goes to /login.
 *
 * Activity is mouse, keyboard, scroll, wheel and touch inside this app.
 */
const IDLE_LIMIT_MS = 15 * 60 * 1000;
const WARNING_MS = 60 * 1000;
const CHECK_EVERY_MS = 1000;
const AUTO_SIGN_OUT_SECONDS = 10;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "wheel", "touchstart"];
const TOKEN_KEYS = ["authToken", "authUser", "access_token", "refresh_token", "token"];

const TEXT: Record<string, Record<string, string>> = {
  en: {
    warningTitle: "You will be signed out soon",
    warningBody: "You have been inactive. For your security you will be signed out in",
    seconds: "seconds",
    stay: "Stay signed in",
    expiredTitle: "Your session has expired",
    expiredBody: "You were signed out after 15 minutes of inactivity. Please sign in again to continue.",
    signIn: "Sign in again",
    autoSignOut: "You will be signed out in",
  },
  am: {
    warningTitle: "በቅርቡ ከሲስተሙ ይወጣሉ",
    warningBody: "ለተወሰነ ጊዜ እንቅስቃሴ አላደረጉም። ለደህንነትዎ ሲባል በሚከተለው ጊዜ ውስጥ ከሲስተሙ ይወጣሉ፦",
    seconds: "ሰከንድ",
    stay: "እንደገቡ ይቆዩ",
    expiredTitle: "ክፍለ ጊዜዎ አብቅቷል",
    expiredBody: "ለ15 ደቂቃ እንቅስቃሴ ባለማድረግዎ ከሲስተሙ ወጥተዋል። ለመቀጠል እባክዎ እንደገና ይግቡ።",
    signIn: "እንደገና ይግቡ",
    autoSignOut: "ከሥርዓቱ የሚወጡት በ",
  },
  or: {
    warningTitle: "Dhiyootti sirna keessaa baatu",
    warningBody: "Yeroo muraasaaf sochii hin goone. Nageenya keessaniif yeroo kana keessatti sirna keessaa baatu:",
    seconds: "sekondii",
    stay: "Seenaa turi",
    expiredTitle: "Yeroon seensa keessanii xumurameera",
    expiredBody: "Daqiiqaa 15f sochii dhabuu keessaniin sirna keessaa baatanii jirtu. Itti fufuuf maaloo irra deebi'aa seenaa.",
    signIn: "Irra deebi'ii seeni",
    autoSignOut: "Sirna keessaa kan baatan",
  },
};

const currentText = () => {
  let lang = "en";
  try {
    lang = (localStorage.getItem("language") || localStorage.getItem("lang") || "en").slice(0, 2);
  } catch {
    // storage unavailable: English
  }
  return TEXT[lang] ?? TEXT.en;
};

const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const hasSession = () => {
  try {
    return TOKEN_KEYS.some((k) => sessionStorage.getItem(k) || localStorage.getItem(k));
  } catch {
    return false;
  }
};

const clearSession = () => {
  try {
    TOKEN_KEYS.forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
  } catch {
    // nothing stored
  }
};

const SessionTimeout: React.FC<{ app: string }> = ({ app }) => {
  const lastActivity = useRef(Date.now());
  const [remaining, setRemaining] = useState<number | null>(null);
  const [expired, setExpired] = useState(false);
  const [signOutIn, setSignOutIn] = useState(AUTO_SIGN_OUT_SECONDS);

  const markActive = useCallback(() => {
    lastActivity.current = Date.now();
  }, []);

  const expire = useCallback(() => {
    clearSession();
    setRemaining(null);
    setExpired(true);
    if (isEmbedded()) {
      window.parent.postMessage({ type: "SESSION_EXPIRED", reason: "inactivity", app }, "*");
    }
  }, [app]);

  useEffect(() => {
    if (expired) return;
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    const timer = window.setInterval(() => {
      if (!hasSession()) {
        lastActivity.current = Date.now();
        setRemaining(null);
        return;
      }
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_LIMIT_MS) {
        expire();
      } else if (idle >= IDLE_LIMIT_MS - WARNING_MS) {
        setRemaining(Math.ceil((IDLE_LIMIT_MS - idle) / 1000));
      } else {
        setRemaining(null);
      }
    }, CHECK_EVERY_MS);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
      window.clearInterval(timer);
    };
  }, [expired, expire, markActive]);

  const signInAgain = useCallback(() => {
    if (isEmbedded()) {
      // LOGOUT is what the portal acts on; the others are kept for listeners
      // that use them.
      window.parent.postMessage({ type: "LOGOUT", reason: "inactivity", app }, "*");
      window.parent.postMessage({ type: "LOGOUT_REQUEST", reason: "inactivity", app }, "*");
    } else {
      window.location.assign("/login");
    }
  }, [app]);

  // Nobody clicked "Sign in again": sign out anyway after a short countdown.
  useEffect(() => {
    if (!expired) return;
    setSignOutIn(AUTO_SIGN_OUT_SECONDS);
    const tick = window.setInterval(() => {
      setSignOutIn((left) => {
        if (left <= 1) {
          window.clearInterval(tick);
          signInAgain();
          return 0;
        }
        return left - 1;
      });
    }, 1000);
    return () => window.clearInterval(tick);
  }, [expired, signInAgain]);

  const text = currentText();

  if (expired) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md overflow-hidden rounded-xl bg-white text-center shadow-2xl">
          <div className="bg-[#AA232F] px-6 py-4 text-lg font-semibold text-white">{text.expiredTitle}</div>
          <p className="px-6 pt-5 text-sm text-gray-700">{text.expiredBody}</p>
          <p className="px-6 pb-5 pt-2 text-xs text-gray-500">
            {text.autoSignOut} {signOutIn} {text.seconds}
          </p>
          <div className="px-6 pb-6">
            <button
              type="button"
              onClick={signInAgain}
              className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              {text.signIn}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (remaining === null) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" role="alertdialog">
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white text-center shadow-2xl">
        <div className="bg-[#AA232F] px-6 py-4 text-lg font-semibold text-white">{text.warningTitle}</div>
        <p className="px-6 pt-5 text-sm text-gray-700">{text.warningBody}</p>
        <p className="py-3 text-4xl font-bold text-[#AA232F]">
          {remaining} <span className="text-base font-medium text-gray-500">{text.seconds}</span>
        </p>
        <div className="px-6 pb-6">
          <button
            type="button"
            onClick={() => {
              markActive();
              setRemaining(null);
            }}
            className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700"
          >
            {text.stay}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeout;
